import React, { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { initializeCustomerDatabase } from "@/lib/db/customerDb";
import AllCustomersTab from "./customer-management/AllCustomersTab";
import SegmentationTab from "./customer-management/SegmentationTab";
import PurchaseBehaviorTab from "./customer-management/PurchaseBehaviorTab";
import CampaignTargetingTab from "./customer-management/CampaignTargetingTab";
import { useToast } from "@/components/ui/use-toast";
import { LayoutLoader } from "@/components/ui/layout-loader";
import { Button } from "@/components/ui/button";

const CustomerManagement = () => {
  const [activeTab, setActiveTab] = useState("all-customers");
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const initialize = async () => {
      try {
        console.log("Initializing customer database...");
        setIsInitializing(true);
        await initializeCustomerDatabase();
        console.log("Customer database initialized successfully");
        setIsInitializing(false);
      } catch (error) {
        console.error("Failed to initialize customer database", error);
        setInitError("Database initialization failed. Try refreshing the page.");
        toast({
          variant: "destructive",
          title: "Database initialization failed",
          description: "There was an error setting up the customer database. Some features may not work correctly.",
        });
        setIsInitializing(false);
      }
    };
    
    initialize();
  }, [toast]);

  if (isInitializing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
        <p className="mt-4 text-lg text-muted-foreground">Initializing customer database...</p>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="text-destructive text-xl">⚠️ {initError}</div>
        <Button 
          className="mt-4" 
          variant="outline" 
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Customer Management</h1>
        <p className="text-muted-foreground">
          Manage customer information, analyze behavior, and target campaigns.
        </p>
      </div>
      
      <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full border-b justify-start">
          <TabsTrigger value="all-customers" className="px-8 py-2">All Customers</TabsTrigger>
          <TabsTrigger value="segmentation" className="px-8 py-2">Segmentation</TabsTrigger>
          <TabsTrigger value="purchase-behavior" className="px-8 py-2">Purchase Behavior</TabsTrigger>
          <TabsTrigger value="campaign-targeting" className="px-8 py-2">Campaign Targeting</TabsTrigger>
        </TabsList>
        
        <TabsContent value="all-customers" className="space-y-4">
          <AllCustomersTab />
        </TabsContent>
        
        <TabsContent value="segmentation" className="space-y-4">
          <SegmentationTab />
        </TabsContent>
        
        <TabsContent value="purchase-behavior" className="space-y-4">
          <PurchaseBehaviorTab />
        </TabsContent>
        
        <TabsContent value="campaign-targeting" className="space-y-4">
          <CampaignTargetingTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CustomerManagement;
